import { useState, useEffect } from "react";
import { Loader2, Palette, Save, Check, Users, Trash2, Crown, Lock, Plus, Link as LinkIcon, Copy, Share2, X, Globe } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function WhitelabelPage() {
  return (
    <DashboardLayout
      eyebrow="Agency mode"
      title="White-label & client workspaces"
      subtitle="Apply your own brand on reports and the client portal. Manage your client roster."
    >
      <Tabs defaultValue="branding">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="whitelabel-tabs">
          <TabsTrigger value="branding" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-branding">
            <Palette className="w-3.5 h-3.5 mr-1.5" /> Branding
          </TabsTrigger>
          <TabsTrigger value="clients" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-clients">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Client workspaces
          </TabsTrigger>
        </TabsList>
        <TabsContent value="branding" className="mt-5"><BrandingTab /></TabsContent>
        <TabsContent value="clients" className="mt-5"><ClientsTab /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function LockedBanner({ reason }) {
  return (
    <div className="bg-amber-950/20 border border-amber-700/40 rounded-sm p-5 flex items-start gap-3 mb-5" data-testid="whitelabel-locked-banner">
      <Crown className="w-5 h-5 text-amber-400 mt-0.5" />
      <div className="flex-1">
        <div className="font-display text-sm text-amber-200 mb-1">Agency-tier feature</div>
        <p className="text-xs text-amber-200/80 leading-relaxed">{reason || "Upgrade to Agency to unlock white-label reports, custom branding and client workspaces."}</p>
        <Link to="/upgrade" className="text-xs text-[#3377FF] hover:text-white underline underline-offset-2 mt-2 inline-block">See plans →</Link>
      </div>
    </div>
  );
}

function BrandingTab() {
  const [form, setForm] = useState({ brand_name: "", logo_url: "", primary_color: "#0055FF", support_email: "", footer_text: "", hide_powered_by: false });
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/whitelabel/branding").then(({ data }) => {
      setEligible(!!data.eligible);
      setForm({
        brand_name: data.brand_name || "",
        logo_url: data.logo_url || "",
        primary_color: data.primary_color || "#0055FF",
        support_email: data.support_email || "",
        footer_text: data.footer_text || "",
        hide_powered_by: !!data.hide_powered_by,
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr(""); setSaved(false);
    try {
      await api.post("/whitelabel/branding", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-3xl">
      {!eligible && <LockedBanner />}
      <form onSubmit={submit} className={`bg-zinc-950 border border-zinc-800 rounded-sm p-6 space-y-4 ${!eligible ? "opacity-70 pointer-events-none select-none" : ""}`} data-testid="branding-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Brand name</Label>
            <Input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} required
                   placeholder="Acme Growth Studio"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="brand-name" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Primary color</Label>
            <div className="flex gap-2">
              <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                     className="h-11 w-16 p-1 bg-zinc-950 border-zinc-800 rounded-sm cursor-pointer" data-testid="brand-color" />
              <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                     className="h-11 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Logo URL</Label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                   placeholder="https://your-cdn.com/logo.png"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="brand-logo" />
            {form.logo_url && (
              <div className="mt-2 inline-block bg-white p-2 rounded-sm">
                <img src={form.logo_url} alt="Brand logo preview" className="h-10 w-auto" data-testid="brand-logo-preview"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Support email</Label>
            <Input type="email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                   placeholder="hello@acme.com"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="brand-support" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Footer text</Label>
            <Input value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                   placeholder="© 2026 Acme Growth Studio"
                   className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="brand-footer" />
          </div>
        </div>
        <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3">
          <div>
            <div className="text-sm text-zinc-200">Hide "Powered by RankPilot" on exports</div>
            <p className="text-xs text-zinc-500 mt-0.5">Removes the small attribution line from PDFs and shared reports.</p>
          </div>
          <Switch checked={form.hide_powered_by} onCheckedChange={(v) => setForm({ ...form, hide_powered_by: v })} data-testid="brand-hide-powered" />
        </div>
        {err && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="branding-error">{err}</div>}
        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={saving || !eligible} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="brand-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : !eligible ? <><Lock className="w-4 h-4 mr-2" /> Locked</> : <><Save className="w-4 h-4 mr-2" /> Save branding</>}
          </Button>
          {saved && <span className="text-sm text-[#10B981] flex items-center gap-1"><Check className="w-4 h-4" /> Saved</span>}
        </div>
      </form>
    </div>
  );
}

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", contact_email: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/whitelabel/clients"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setClients(c.data.items || []);
      setEligible(!!c.data.eligible);
      setProjects(p.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/whitelabel/clients", form);
      setForm({ name: "", contact_email: "", notes: "" });
      await load();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this client workspace?")) return;
    await api.delete(`/whitelabel/clients/${id}`);
    await load();
  };

  if (loading) return <div className="flex items-center gap-2 text-zinc-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      {!eligible && <LockedBanner />}
      <form onSubmit={add} className={`bg-zinc-950 border border-zinc-800 rounded-sm p-6 grid sm:grid-cols-3 gap-3 items-end ${!eligible ? "opacity-70 pointer-events-none select-none" : ""}`} data-testid="clients-form">
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Client name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                 placeholder="Acme Dental"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="client-name" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Contact email</Label>
          <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                 placeholder="owner@acme.com"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="client-email" />
        </div>
        <div>
          <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Notes</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                 placeholder="Retainer · weekly autopilot"
                 className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="client-notes" />
        </div>
        <div className="sm:col-span-3">
          <Button type="submit" disabled={busy || !eligible} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="client-add">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Add client</>}
          </Button>
        </div>
      </form>
      {err && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="clients-error">{err}</div>}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="clients-list">
        <div className="px-4 py-3 border-b border-zinc-800 text-xs uppercase tracking-widest text-zinc-500 flex justify-between">
          <span>Your clients</span>
          <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400">{clients.length} / 25</Badge>
        </div>
        {clients.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No clients yet. Add your first one above.</div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {clients.map((c) => (
              <ClientRow key={c.client_id} client={c} projects={projects} onChange={load} onRemove={() => remove(c.client_id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ClientRow({ client, projects, onChange, onRemove }) {
  // Local copy so we can optimistically reflect share state without waiting for parent re-fetch
  const [c, setC] = useState(client);
  useEffect(() => { setC(client); }, [client]);
  const [shareUrl, setShareUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachId, setAttachId] = useState("");
  const attachedIds = c.project_ids || [];
  const attachedProjects = projects.filter((p) => attachedIds.includes(p.id));
  const candidates = projects.filter((p) => !attachedIds.includes(p.id));

  const share = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/whitelabel/clients/${c.client_id}/share`);
      setShareUrl(data.url);
      setC((prev) => ({ ...prev, share_token: data.token, share_enabled: true }));
      try { await navigator.clipboard.writeText(data.url); toast.success("Portal link copied to clipboard"); } catch {}
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this portal link? The client will no longer be able to access it.")) return;
    await api.delete(`/whitelabel/clients/${c.client_id}/share`);
    setShareUrl("");
    setC((prev) => ({ ...prev, share_token: null, share_enabled: false }));
    onChange();
  };

  const attach = async () => {
    if (!attachId) return;
    setBusy(true);
    try {
      await api.post(`/whitelabel/clients/${c.client_id}/projects/${attachId}`);
      setC((prev) => ({ ...prev, project_ids: [...(prev.project_ids || []), attachId] }));
      setAttachId("");
      onChange();
    } finally { setBusy(false); }
  };

  const detach = async (pid) => {
    setBusy(true);
    try {
      await api.delete(`/whitelabel/clients/${c.client_id}/projects/${pid}`);
      setC((prev) => ({ ...prev, project_ids: (prev.project_ids || []).filter((x) => x !== pid) }));
      onChange();
    } finally { setBusy(false); }
  };

  const copyExisting = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Copied"); } catch {}
  };

  const hasToken = !!c.share_token && c.share_enabled !== false;

  return (
    <div className="px-5 py-4 space-y-3" data-testid={`client-row-${c.client_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-zinc-200 font-medium">{c.name}</div>
          <div className="text-xs text-zinc-500 truncate">{c.contact_email || "—"} {c.notes ? `· ${c.notes}` : ""}</div>
        </div>
        {hasToken ? (
          <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm">Portal active</Badge>
        ) : (
          <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500">No portal</Badge>
        )}
        <Button onClick={onRemove} variant="outline" size="sm"
                className="bg-zinc-950 border-red-900/40 hover:bg-red-950/30 text-red-400 rounded-sm" data-testid={`client-remove-${c.client_id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-1">Projects</span>
        {attachedProjects.length === 0 && <span className="text-xs text-zinc-600">None attached</span>}
        {attachedProjects.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1.5 bg-[#0055FF]/10 border border-[#0055FF]/30 text-[#3377FF] rounded-sm px-2 py-1 text-xs" data-testid={`client-project-${c.client_id}-${p.id}`}>
            <Globe className="w-3 h-3" />{p.name}
            <button onClick={() => detach(p.id)} className="ml-1 hover:text-red-400" aria-label="Detach"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {candidates.length > 0 && (
          <div className="flex items-center gap-1.5 ml-auto">
            <Select value={attachId} onValueChange={setAttachId}>
              <SelectTrigger className="h-8 w-44 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid={`client-attach-select-${c.client_id}`}>
                <SelectValue placeholder="Attach project…" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                {candidates.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">{p.name} · {p.domain}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={attach} disabled={!attachId || busy} size="sm" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-8 text-xs" data-testid={`client-attach-btn-${c.client_id}`}>
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {!hasToken ? (
          <Button onClick={share} disabled={busy} size="sm" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-9" data-testid={`client-share-${c.client_id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5 mr-1.5" />}
            Create portal link
          </Button>
        ) : (
          <>
            <Button onClick={share} disabled={busy} size="sm" variant="outline"
                    className="bg-zinc-950 border-zinc-800 hover:bg-zinc-900 rounded-sm h-9" data-testid={`client-copy-${c.client_id}`}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy portal link
            </Button>
            <Button onClick={revoke} size="sm" variant="outline"
                    className="bg-zinc-950 border-red-900/40 hover:bg-red-950/30 text-red-400 rounded-sm h-9" data-testid={`client-revoke-${c.client_id}`}>
              <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> Revoke
            </Button>
          </>
        )}
        {shareUrl && (
          <code className="flex-1 min-w-0 truncate text-xs bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1.5 text-zinc-400" onClick={copyExisting} title="Click to copy" data-testid={`client-share-url-${c.client_id}`}>
            {shareUrl}
          </code>
        )}
      </div>
    </div>
  );
}
